import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ref, get, set, push } from 'firebase/database';
import { database } from '../../firebase';
import TemplateEditorDetailView from './TemplateEditorDetailView';

const TASK_TEAM_ROLE_KEYS = ['hr', 'it', 'legal', 'finance'];

const normalizeRosterText = (value) => String(value || '').trim().toLowerCase();

const normalizeRosterRoleKey = (value) => {
    const normalized = normalizeRosterText(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (normalized === 'human_resources' || normalized === 'people_ops' || normalized === 'people_operations') return 'hr';
    if (normalized === 'information_technology' || normalized === 'systems') return 'it';
    if (normalized === 'legal_team') return 'legal';
    if (normalized === 'finance_accounting' || normalized === 'accounting') return 'finance';
    return normalized;
};

const sanitizeRosterKey = (value) => String(value || 'template')
    .replace(/[.#$/[\]\s]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Template Manager Modal
 * Manages onboarding templates:
 * - Main template (applies to all departments)
 * - Department-specific templates
 */
const TemplateManagerModal = ({
    isOpen,
    onClose,
    companyUID,
    teamMembers = [],
    externalContacts = [],
    onSaveExternalContact,
    onRemoveExternalContact,
    canManageTemplates = true,
    t = (key, fallback) => fallback || key
}) => {
  useUiLanguage();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [showTemplateEditor, setShowTemplateEditor] = useState(false);
    const [managerStep, setManagerStep] = useState('choice');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const migratedLegacyTeamEmails = useRef(new Set());

    const legacyTemplateTeamContacts = useMemo(() => {
        const contacts = [];
        templates.forEach(template => {
            Object.entries(template?.teamRoles || {}).forEach(([roleKey, people]) => {
                const normalizedRole = normalizeRosterRoleKey(roleKey);
                if (!TASK_TEAM_ROLE_KEYS.includes(normalizedRole) || !Array.isArray(people)) return;

                people.forEach((person, index) => {
                    const email = String(person?.email || '').trim().toLowerCase();
                    const name = String(person?.name || person?.displayName || person?.email || '').trim();
                    if (!email || !name) return;

                    contacts.push({
                        ...person,
                        id: person?.id || `legacy_${sanitizeRosterKey(template?.id)}_${normalizedRole}_${index}`,
                        name,
                        email,
                        department: person?.department || person?.role || normalizedRole.toUpperCase(),
                        roleCategory: person?.roleCategory || normalizedRole,
                        source: person?.source || 'template_team',
                        legacyTemplateContact: true
                    });
                });
            });
        });

        const seen = new Set();
        return contacts.filter(contact => {
            const email = normalizeRosterText(contact.email);
            if (!email || seen.has(email)) return false;
            seen.add(email);
            return true;
        });
    }, [templates]);

    const sharedTemplateExternalContacts = useMemo(() => {
        const seen = new Set();
        return [...externalContacts, ...legacyTemplateTeamContacts].filter(contact => {
            const email = normalizeRosterText(contact?.email);
            if (!email || seen.has(email)) return false;
            seen.add(email);
            return true;
        });
    }, [externalContacts, legacyTemplateTeamContacts]);

    const DEPARTMENT_TRANSLATION_KEYS = {
        Administration: 'administration',
        'Customer Support': 'customerSupport',
        Engineering: 'engineering',
        'Product Management': 'productManagement',
        'Design / UX': 'designUx',
        'Data & Analytics': 'dataAnalytics',
        Marketing: 'marketing',
        Sales: 'sales',
        'Finance & Accounting': 'financeAccounting',
        'Human Resources': 'hr',
        Operations: 'operations',
        'IT / Systems': 'itSystems',
        'Security / InfoSec': 'security',
        Legal: 'legal',
        Compliance: 'compliance',
        Procurement: 'procurement',
        'Supply Chain': 'supplyChain',
        Logistics: 'logistics',
        Manufacturing: 'manufacturing',
        'Quality Assurance': 'qualityAssurance',
        'Research & Development': 'researchDevelopment',
        'Business Development': 'businessDevelopment',
        Partnerships: 'partnerships',
        Strategy: 'strategy',
        Content: 'content',
        'Communications / PR': 'communications',
        Community: 'community',
        Facilities: 'facilities',
        'Real Estate': 'realEstate',
        Construction: 'construction',
        Healthcare: 'healthcare',
        Education: 'education'
    };

    const getDepartmentLabel = (department) => {
        if (department === 'all') {
            return t('templatesManager.allDepartmentsMain', 'All Departments (Main Template)');
        }
        const translationKey = DEPARTMENT_TRANSLATION_KEYS[department];
        return translationKey ? t(`departments.${translationKey}`, department) : department;
    };

    const DEPARTMENTS = [
        { value: 'all', label: getDepartmentLabel('all') },

        // Full department set (matches Home.js job posting dropdown)
        { value: 'Administration', label: getDepartmentLabel('Administration') },
        { value: 'Customer Support', label: getDepartmentLabel('Customer Support') },
        { value: 'Engineering', label: getDepartmentLabel('Engineering') },
        { value: 'Product Management', label: getDepartmentLabel('Product Management') },
        { value: 'Design / UX', label: getDepartmentLabel('Design / UX') },
        { value: 'Data & Analytics', label: getDepartmentLabel('Data & Analytics') },
        { value: 'Marketing', label: getDepartmentLabel('Marketing') },
        { value: 'Sales', label: getDepartmentLabel('Sales') },
        { value: 'Finance & Accounting', label: getDepartmentLabel('Finance & Accounting') },
        { value: 'Human Resources', label: getDepartmentLabel('Human Resources') },
        { value: 'Operations', label: getDepartmentLabel('Operations') },
        { value: 'IT / Systems', label: getDepartmentLabel('IT / Systems') },
        { value: 'Security / InfoSec', label: getDepartmentLabel('Security / InfoSec') },
        { value: 'Legal', label: getDepartmentLabel('Legal') },
        { value: 'Compliance', label: getDepartmentLabel('Compliance') },
        { value: 'Procurement', label: getDepartmentLabel('Procurement') },
        { value: 'Supply Chain', label: getDepartmentLabel('Supply Chain') },
        { value: 'Logistics', label: getDepartmentLabel('Logistics') },
        { value: 'Manufacturing', label: getDepartmentLabel('Manufacturing') },
        { value: 'Quality Assurance', label: getDepartmentLabel('Quality Assurance') },
        { value: 'Research & Development', label: getDepartmentLabel('Research & Development') },
        { value: 'Business Development', label: getDepartmentLabel('Business Development') },
        { value: 'Partnerships', label: getDepartmentLabel('Partnerships') },
        { value: 'Strategy', label: getDepartmentLabel('Strategy') },
        { value: 'Content', label: getDepartmentLabel('Content') },
        { value: 'Communications / PR', label: getDepartmentLabel('Communications / PR') },
        { value: 'Community', label: getDepartmentLabel('Community') },
        { value: 'Facilities', label: getDepartmentLabel('Facilities') },
        { value: 'Real Estate', label: getDepartmentLabel('Real Estate') },
        { value: 'Construction', label: getDepartmentLabel('Construction') },
        { value: 'Healthcare', label: getDepartmentLabel('Healthcare') },
        { value: 'Education', label: getDepartmentLabel('Education') }
    ];

    const STAGES = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'];

    const sanitizeForFirebase = (value) => {
        if (value === undefined || typeof value === 'function') return undefined;

        // Remove browser File/Blob objects from payloads
        if (typeof File !== 'undefined' && value instanceof File) return undefined;
        if (typeof Blob !== 'undefined' && value instanceof Blob) return undefined;

        if (Array.isArray(value)) {
            return value
                .map(item => sanitizeForFirebase(item))
                .filter(item => item !== undefined);
        }

        if (value && typeof value === 'object') {
            const out = {};
            Object.entries(value).forEach(([key, val]) => {
                if (key === 'file') return; // local UI-only field from upload widgets
                const cleaned = sanitizeForFirebase(val);
                if (cleaned !== undefined) {
                    out[key] = cleaned;
                }
            });
            return out;
        }

        return value;
    };

    // Load templates
    useEffect(() => {
        if (isOpen && companyUID) {
            setManagerStep('choice');
            setSelectedDepartment('');
            setEditingTemplate(null);
            setShowTemplateEditor(false);
            loadTemplates();
        }
    }, [isOpen, companyUID]);

    useEffect(() => {
        if (!isOpen || !canManageTemplates || !onSaveExternalContact || legacyTemplateTeamContacts.length === 0) return;

        const sharedEmails = new Set(externalContacts.map(contact => normalizeRosterText(contact?.email)).filter(Boolean));
        legacyTemplateTeamContacts.forEach(contact => {
            const email = normalizeRosterText(contact.email);
            if (!email || sharedEmails.has(email) || migratedLegacyTeamEmails.current.has(email)) return;

            migratedLegacyTeamEmails.current.add(email);
            const payload = { ...contact };
            delete payload.id;
            delete payload.legacyTemplateContact;
            onSaveExternalContact({
                ...payload,
                source: 'external_contact',
                roleCategory: normalizeRosterRoleKey(payload.roleCategory)
            });
        });
    }, [isOpen, canManageTemplates, externalContacts, legacyTemplateTeamContacts, onSaveExternalContact]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const templatesRef = ref(database, `companies/${companyUID}/onboardingTemplates`);
            const snapshot = await get(templatesRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const templatesArray = Object.entries(data).map(([id, template]) => ({
                    id,
                    ...template
                }));
                setTemplates(templatesArray);
            } else {
                // Create default main template if none exists
                const defaultTemplate = createDefaultTemplate();
                setTemplates([defaultTemplate]);
            }
        } catch (error) {
            console.error('Error loading templates:', error);
        }
        setLoading(false);
    };

    const createDefaultTemplate = () => ({
        id: 'main',
        name: 'Main Onboarding Template',
        department: 'all',
        stages: {
            'Before Day One': {
                tasks: [
                    {
                        id: 'task_def_1',
                        title: 'Send welcome email',
                        description: 'Send personalized welcome email to new hire',
                        relativeDays: 0,
                        type: 'automated',
                        automationType: 'document_send',
                        executionMode: 'immediate',
                        emailDraft: {
                            subject: 'Welcome to the team!',
                            body: 'Hi [Name],\n\nWe are thrilled to welcome you to our team! Your start date is confirmed.\n\nWe\'ll be in touch with more details soon.\n\nBest regards,\nThe Team'
                        }
                    },
                    { id: 'task_def_2', title: 'Prepare workstation', description: 'Set up desk, computer, and necessary equipment', relativeDays: -3, assigneeCategory: 'it' },
                    { id: 'task_def_3', title: 'Create email account', description: 'Set up company email and necessary accounts', relativeDays: -3, assigneeCategory: 'it' },
                    { id: 'task_def_4', title: 'Prepare welcome kit', description: 'Assemble welcome kit with company swag and materials', relativeDays: -2, assigneeCategory: 'hr' },
                    { id: 'task_def_5', title: 'Schedule orientation', description: 'Book rooms and send calendar invites for Day 1', relativeDays: -2, assigneeCategory: 'hr' },
                    {
                        id: 'task_def_6',
                        title: 'Request pre-boarding documents',
                        description: 'Requested pre-boarding documents',
                        relativeDays: -5,
                        type: 'automated',
                        automationType: 'document_request',
                        requiredDocuments: ['Government ID', 'Bank details', 'Emergency contacts']
                    }
                ]
            },
            'Day One': {
                tasks: [
                    { id: 'task_def_7', title: 'Welcome and office tour', description: 'Greet new hire and show them around the office', relativeDays: 0, assigneeCategory: 'hr' },
                    { id: 'task_def_8', title: 'IT setup', description: 'Help with computer login, software installation, and access', relativeDays: 0, assigneeCategory: 'it' },
                    { id: 'task_def_9', title: 'Team introduction', description: 'Introduce to immediate team members', relativeDays: 0, assigneeCategory: 'manager' },
                    { id: 'task_def_10', title: 'Review policies and handbook', description: 'Go through key company policies and expectations', relativeDays: 0, assigneeCategory: 'hr' },
                    { id: 'task_def_11', title: 'Lunch with team', description: 'Arrange team lunch or coffee', relativeDays: 0, assigneeCategory: 'manager' }
                ]
            },
            'First Week': {
                tasks: [
                    { id: 'task_def_12', title: 'Department overview meeting', description: 'Schedule meeting with department head', relativeDays: 2, assigneeCategory: 'manager' },
                    { id: 'task_def_13', title: 'Set up 1-on-1s with key stakeholders', description: 'Schedule introductory meetings with cross-functional partners', relativeDays: 3, assigneeCategory: 'manager' },
                    { id: 'task_def_14', title: 'Assign initial training modules', description: 'Provide access to role-specific training materials', relativeDays: 1, assigneeCategory: 'hr' },
                    { id: 'task_def_15', title: 'Review job responsibilities', description: 'Detailed walkthrough of role expectations and goals', relativeDays: 2, assigneeCategory: 'manager' },
                    { id: 'task_def_16', title: 'First week check-in', description: 'Scheduled feedback session to address questions', relativeDays: 5, assigneeCategory: 'manager' }
                ]
            },
            'First Month': {
                tasks: [
                    { id: 'task_def_17', title: 'Set 30-day goals', description: 'Define clear objectives for the first month', relativeDays: 7, assigneeCategory: 'manager' },
                    { id: 'task_def_18', title: 'Assign first project', description: 'Provide meaningful work assignment', relativeDays: 10, assigneeCategory: 'manager' },
                    { id: 'task_def_19', title: 'Schedule regular 1-on-1s', description: 'Establish recurring check-in meetings', relativeDays: 7, assigneeCategory: 'manager' },
                    { id: 'task_def_20', title: '30-day feedback session', description: 'Comprehensive check-in on progress and concerns', relativeDays: 30, assigneeCategory: 'hr' }
                ]
            },
            '90-Day Review': {
                tasks: [
                    { id: 'task_def_21', title: 'Performance review preparation', description: 'Gather feedback from team and prepare review', relativeDays: 85, assigneeCategory: 'manager' },
                    { id: 'task_def_22', title: '90-day performance review', description: 'Formal review of progress and integration', relativeDays: 90, assigneeCategory: 'hr' },
                    { id: 'task_def_23', title: 'Set quarterly goals', description: 'Define objectives for next quarter', relativeDays: 90, assigneeCategory: 'manager' }
                ]
            }
        },
        isDefault: true
    });

    const handleEditTemplate = (template) => {
        if (!canManageTemplates) return;
        setEditingTemplate({ ...template });
        setShowTemplateEditor(true);
    };

    const getMainTemplate = () => (
        templates.find(template => template.department === 'all' || template.isDefault)
        || createDefaultTemplate()
    );

    const createEmptyDepartmentTemplate = (department) => {
        const departmentLabel = getDepartmentLabel(department);
        return {
            id: `department_${String(department).replace(/[^a-z0-9]+/gi, '_').toLowerCase()}`,
            name: t('templatesManager.departmentTemplateName', {
                defaultValue: '{{department}} Onboarding Template',
                department: departmentLabel
            }),
            department,
            stages: STAGES.reduce((acc, stage) => {
                acc[stage] = { tasks: [] };
                return acc;
            }, {}),
            isNew: true
        };
    };

    const handleContinueDepartmentTemplate = () => {
        if (!canManageTemplates) return;
        if (!selectedDepartment) return;
        const existingTemplate = templates.find(template => (
            template.department === selectedDepartment && !template.isDefault
        ));
        handleEditTemplate(existingTemplate || createEmptyDepartmentTemplate(selectedDepartment));
    };

    const handleSaveTemplate = async (template) => {
        if (!canManageTemplates) return;
        try {
            let templateRef;
            if (template.isNew || !template.id) {
                templateRef = push(ref(database, `companies/${companyUID}/onboardingTemplates`));
            } else {
                templateRef = ref(database, `companies/${companyUID}/onboardingTemplates/${template.id}`);
            }

            const { isNew, id, ...templateData } = template;
            const cleanedTemplateData = sanitizeForFirebase(templateData);
            await set(templateRef, {
                ...cleanedTemplateData,
                updatedAt: Date.now()
            });

            setShowTemplateEditor(false);
            setEditingTemplate(null);
            loadTemplates();
        } catch (error) {
            console.error('Error saving template:', error);
            alert(t('alerts.saveTemplateFailedDetailed', {
                defaultValue: 'Failed to save template: {{message}}',
                message: error?.message || 'Unknown error'
            }));
        }
    };

    const handleDeleteTemplate = async (template) => {
        if (!canManageTemplates || !template?.id || template.department === 'all' || template.isDefault) return;
        if (!window.confirm(t('templatesManager.deleteTemplateConfirm', 'Are you sure you want to delete this template?'))) return;

        try {
            await set(ref(database, `companies/${companyUID}/onboardingTemplates/${template.id}`), null);
            setTemplates(current => current.filter(item => item.id !== template.id));
            if (selectedDepartment === template.department) setSelectedDepartment('');
        } catch (error) {
            console.error('Error deleting template:', error);
            alert(t('alerts.deleteTemplateFailed', 'Failed to delete template. Please try again.'));
        }
    };

    if (!isOpen || !canManageTemplates) return null;

    return (
        <div className="modal-overlay template-manager-overlay">
            <div className="template-manager-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-state">
                            <i className="fa-solid fa-spinner fa-spin"></i>
                            <p>{t('templatesManager.loading', 'Loading templates...')}</p>
                        </div>
                    ) : showTemplateEditor ? (
                        <TemplateEditorDetailView
                            template={editingTemplate}
                            departments={DEPARTMENTS}
                            stages={STAGES}
                            onSave={handleSaveTemplate}
                            onClose={() => {
                                setShowTemplateEditor(false);
                                setEditingTemplate(null);
                            }}
                            companyUID={companyUID}
                            teamMembers={teamMembers}
                            externalContacts={sharedTemplateExternalContacts}
                            onSaveExternalContact={onSaveExternalContact}
                            onRemoveExternalContact={onRemoveExternalContact}
                            t={t}
                        />
                    ) : (
                        managerStep === 'department' ? (
                            <div className="template-manager-screen template-manager-department-screen">
                                <button className="template-manager-back" onClick={() => setManagerStep('choice')}>
                                    <i className="fa-solid fa-arrow-left"></i>
                                    {t('templatesManager.back', 'Back')}
                                </button>

                                <div className="template-department-picker">
                                    <h2>{t('templatesManager.selectDepartmentTitle', 'Select a Department')}</h2>
                                    <select
                                        value={selectedDepartment}
                                        onChange={event => setSelectedDepartment(event.target.value)}
                                        className="template-department-select"
                                    >
                                        <option value="">{t('templatesManager.selectDepartmentPlaceholder', 'Select department')}</option>
                                        {DEPARTMENTS.filter(dept => dept.value !== 'all').map(dept => (
                                            <option key={dept.value} value={dept.value}>{uiText(dept.label)}</option>
                                        ))}
                                    </select>
                                    <button
                                        className="template-manager-continue"
                                        onClick={handleContinueDepartmentTemplate}
                                        disabled={!selectedDepartment}
                                    >
                                        {t('buttons.continue', 'Continue')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="template-manager-screen">
                                <button className="template-manager-back" onClick={onClose}>
                                    <i className="fa-solid fa-arrow-left"></i>
                                    {t('templatesManager.back', 'Back')}
                                </button>

                                <div className="builder-library">
                                    <h2>{t('builder.library', 'Onboarding templates')}</h2>
                                    <p>{t('builder.libraryHint', 'Open a template to edit its tasks, owners, and timing.')}</p>
                                    <div className="builder-library-list">
                                        {[getMainTemplate(), ...templates.filter(item => item.department !== 'all' && !item.isDefault)].map(item => (
                                            <div className="builder-library-item" key={item.id}>
                                                <button className="builder-library-open" onClick={() => handleEditTemplate(item)}>
                                                    <span><strong>{item.department === 'all' ? t('builder.default', 'Company default') : getDepartmentLabel(item.department)}</strong>
                                                    <small>{item.department === 'all' ? t('builder.fallback', 'Used for departments without their own template') : item.name}</small></span>
                                                    <span>{Object.values(item.stages || {}).reduce((sum, stage) => sum + (stage.tasks?.length || 0), 0)} {t('builder.tasks', 'tasks')} <i className="fa-solid fa-arrow-right" /></span>
                                                </button>
                                                {item.department !== 'all' && !item.isDefault && (
                                                    <button className="builder-library-delete" onClick={() => handleDeleteTemplate(item)} aria-label={`${t('templatesManager.deleteTemplate', 'Delete template')}: ${getDepartmentLabel(item.department)}`} title={t('templatesManager.deleteTemplate', 'Delete template')}>
                                                        <i className="fa-solid fa-trash-can" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="builder-library-create">
                                        <label>{t('builder.department', 'Create a department template')}<select value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)}>
                                            <option value="">{t('templatesManager.selectDepartmentPlaceholder', 'Select department')}</option>
                                            {DEPARTMENTS.filter(dept => dept.value !== 'all').map(dept => <option key={dept.value} value={dept.value}>{uiText(dept.label)}</option>)}
                                        </select></label>
                                        <button disabled={!selectedDepartment} onClick={handleContinueDepartmentTemplate}>{t('builder.blank', 'Start blank / open existing')}</button>
                                        <button disabled={!selectedDepartment || templates.some(item => item.department === selectedDepartment)} onClick={() => handleEditTemplate({ ...createEmptyDepartmentTemplate(selectedDepartment), stages: JSON.parse(JSON.stringify(getMainTemplate().stages)) })}>{t('builder.copy', 'Copy company default')}</button>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateManagerModal;
